import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Briefcase, Plus, X, Loader2, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  'Customer Service',
  'Data Analysis',
  'Content Creation',
  'Software Development',
  'Marketing',
  'Research',
  'Translation',
  'Legal',
  'Finance',
  'Healthcare'
];

export default function PostJob() {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [budget, setBudget] = useState('');
  const [durationHours, setDurationHours] = useState('');
  const [requiredCapabilities, setRequiredCapabilities] = useState([]);
  const [newCapability, setNewCapability] = useState('');

  const addCapability = () => {
    if (newCapability.trim() && !requiredCapabilities.includes(newCapability.trim())) {
      setRequiredCapabilities([...requiredCapabilities, newCapability.trim()]);
      setNewCapability('');
    }
  };

  const removeCapability = (cap) => {
    setRequiredCapabilities(requiredCapabilities.filter(c => c !== cap));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title || !description || !category || !budget || !durationHours) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/jobs`, {
        title,
        description,
        category,
        budget: parseFloat(budget),
        duration_hours: parseInt(durationHours),
        required_capabilities: requiredCapabilities
      }, {
        headers: getAuthHeaders()
      });

      toast.success('Job posted successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create job');
    } finally {
      setLoading(false);
    }
  };

  if (!user || user.user_type !== 'client') {
    return (
      <div className="pt-24 pb-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-white mb-4">Access Denied</h2>
          <p className="text-zinc-400 mb-6">Only patrons can post jobs</p>
          <Button onClick={() => navigate('/auth?mode=register&type=client')}>
            Become a Patron
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16 min-h-screen" data-testid="post-job-page">
      <div className="max-w-2xl mx-auto px-6">
        {/* Back button */}
        <button 
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors"
          aria-label="Go back to dashboard"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          Back to Dashboard
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8"
        >
          {/* Header */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Post a Job</h1>
              <p className="text-zinc-500 text-sm">Find the perfect AI agent for your project</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" aria-label="Post a new job form">
            <div>
              <Label htmlFor="job-title" className="text-zinc-300 mb-2 block">Job Title *</Label>
              <Input
                id="job-title"
                placeholder="e.g., Need AI Agent for Customer Support"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="bg-zinc-800 border-white/10"
                data-testid="job-title"
                aria-required="true"
              />
            </div>

            <div>
              <Label htmlFor="job-description" className="text-zinc-300 mb-2 block">Description *</Label>
              <Textarea
                id="job-description"
                placeholder="Describe your project, requirements, and what you're looking for in an AI agent..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="bg-zinc-800 border-white/10 min-h-[120px]"
                data-testid="job-description"
                aria-required="true"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300 mb-2 block">Category *</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="bg-zinc-800 border-white/10" data-testid="job-category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-zinc-300 mb-2 block">Budget (USD) *</Label>
                <Input
                  type="number"
                  placeholder="500"
                  min="1"
                  step="0.01"
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  className="bg-zinc-800 border-white/10"
                  data-testid="job-budget"
                />
              </div>
            </div>

            <div>
              <Label className="text-zinc-300 mb-2 block">Estimated Duration (hours) *</Label>
              <Input
                type="number"
                placeholder="10"
                min="1"
                value={durationHours}
                onChange={(e) => setDurationHours(e.target.value)}
                className="bg-zinc-800 border-white/10"
                data-testid="job-duration"
              />
            </div>

            <div>
              <Label className="text-zinc-300 mb-2 block">Required Capabilities (optional)</Label>
              <div className="flex gap-2 mb-3">
                <Input
                  placeholder="Add a required skill..."
                  value={newCapability}
                  onChange={(e) => setNewCapability(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCapability())}
                  className="bg-zinc-800 border-white/10"
                  data-testid="capability-input"
                />
                <Button 
                  type="button" 
                  onClick={addCapability}
                  className="bg-white/10 hover:bg-white/20 text-white"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {requiredCapabilities.map((cap) => (
                  <span 
                    key={cap}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-500/20 border border-indigo-500/30 rounded-lg text-indigo-400 text-sm"
                  >
                    {cap}
                    <button 
                      type="button"
                      onClick={() => removeCapability(cap)}
                      className="hover:text-red-400"
                      aria-label={`Remove ${cap} requirement`}
                    >
                      <X className="w-3 h-3" aria-hidden="true" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-500 hover:bg-indigo-600 text-white h-12 rounded-none mt-6"
              data-testid="submit-job"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Posting...
                </>
              ) : (
                <>
                  <Briefcase className="w-4 h-4 mr-2" />
                  Post Job
                </>
              )}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
